import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import image from '../logo.png';

import * as apiUtil from '../../util/apiUtil';
import * as tokenUtil from '../../util/tokenUtil';
import * as stringUtil from '../../util/stringUtil';

/* ─────────────────────────── 타입 ─────────────────────────── */

interface SubNavItem {
  label: string;
  path: string;
  isActive: (pathname: string) => boolean;
}

interface NavItem {
  label: string;
  path: string;
  isActive: (pathname: string) => boolean;
  subItems: SubNavItem[];
}

/* ─────────────────────────── 메뉴 설정 ─────────────────────────── */

const ENABLE_KAKAO_MOMENT_MENU = true;

/**
 * 메뉴 구조 변경 시 이 배열만 수정합니다.
 * - 추천 메시지 발송 + 셀프 메시지 등록 통합 → SKT 메시지로 통합
 * - 카카오 모먼트 → 기존 셀프 메시지 등록 위치로 이동
 * - URL 변경 → path / isActive 만 수정
 */
const BASE_NAV_ITEMS: NavItem[] = [
  {
    label: '서비스 소개',
    path: '/intro',
    isActive: (p) => p.startsWith('/intro'),
    subItems: [
      { label: '서비스 소개', path: '/intro', isActive: (p) => p.startsWith('/intro') },
    ],
  },
  {
    label: 'SKT 메시지',
    path: '/selfmessage/create',
    isActive: (p) => p.startsWith('/selfmessage') || p.startsWith('/recommend') || p.startsWith('/form'),
    subItems: [
      { label: '셀프 메시지 등록', path: '/selfmessage/create', isActive: (p) => p === '/selfmessage/create' },
      { label: '추천 메시지 등록', path: '/recommend', isActive: (p) => p.startsWith('/recommend') },
      { label: '이미지 관리', path: '/selfmessage/image', isActive: (p) => p === '/selfmessage/image' },
      { label: '설문 관리', path: '/form/list', isActive: (p) => p.startsWith('/form') },
      { label: 'N-PASS', path: '/attention', isActive: (p) => p.startsWith('/attention') },
    ],
  },
  {
    label: '카카오모먼트',
    path: '/moment/index',
    isActive: (p) => p.startsWith('/moment'),
    subItems: [
      { label: '서비스 연동', path: '/moment/description', isActive: (p) => p === '/moment/description' },
      { label: '메시지 등록', path: '/moment/create', isActive: (p) => p === '/moment/create' },
      { label: '메시지 관리', path: '/moment/list', isActive: (p) => p === '/moment/list' },
    ],
  },
  {
    label: '발송 메시지 확인',
    path: '/sent/list',
    isActive: (p) => p.startsWith('/sent'),
    subItems: [
      { label: '발송 메시지 확인', path: '/sent/list', isActive: (p) => p.startsWith('/sent') },
    ],
  },
  {
    label: '충전',
    path: '/cash/charge',
    isActive: (p) => p.startsWith('/cash'),
    subItems: [
      { label: '충전', path: '/cash/charge', isActive: (p) => p === '/cash/charge' },
      { label: '충전 내역', path: '/cash/history', isActive: (p) => p.startsWith('/cash/history') },
    ],
  },
  {
    label: '고객 센터',
    path: '/announce',
    isActive: (p) => p.startsWith('/announce'),
    subItems: [
      { label: '공지사항', path: '/announce', isActive: (p) => p === '/announce' },
      { label: 'FAQ', path: '/announce/faq', isActive: (p) => p.startsWith('/announce/faq') },
      { label: '1:1 문의', path: '/announce/inquiry/create', isActive: (p) => p.startsWith('/announce/inquiry/create') },
      { label: '나의 문의', path: '/announce/inquiry', isActive: (p) => p.startsWith('/announce/inquiry') && !p.includes('/create') },
    ],
  },

];

const NAV_ITEMS: NavItem[] = ENABLE_KAKAO_MOMENT_MENU
  ? BASE_NAV_ITEMS
  : BASE_NAV_ITEMS.filter((item) => item.label !== '카카오모먼트');

/* ─────────────────────────── 컴포넌트 ─────────────────────────── */

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [windowPath, setWindowPath] = useState(location.pathname);
  const [isLoggedIn] = useState(!!tokenUtil.getAccessToken());
  const [points, setPoints] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);

  const accountRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 메가메뉴 배경 높이: 서브아이템 개수 + 상하 여백을 포함해 넉넉하게 계산
  const maxCount = Math.max(...NAV_ITEMS.map((n) => n.subItems.length));
  const megaMenuHeight = Math.max(maxCount * 44 + 44, 220);

  const refreshPoints = () => {
    if (!isLoggedIn) return;
    apiUtil.asyncRequest({
      url: '/api/bizchat/user/info/message',
      method: apiUtil.requestMethod.POST,
      success: (result: any) => {
        setPoints(result.amount + result.freeAmount);
      },
    });
  };

  useEffect(() => {
    setWindowPath(location.pathname);
    setMobileOpen(false);
    refreshPoints();
  }, [location]);

  useEffect(() => {
    const handlePointRefresh = () => refreshPoints();
    window.addEventListener("bizchat:point-refresh", handlePointRefresh);
    return () => window.removeEventListener("bizchat:point-refresh", handlePointRefresh);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const movePage = (uri: string) => {
    if (
      !isLoggedIn &&
      !(
        uri.startsWith('/intro') ||
        uri.startsWith('/moment') ||
        uri.startsWith('/account/login') ||
        uri.startsWith('/register') ||
        uri.startsWith('/cash/charge')
      )
    ) {
      alert('로그인 후 이용 가능한 서비스 입니다!');
    }
    navigate(uri);
  };

  /* 메가메뉴 호버 — 메뉴↔메가메뉴 이동 시 깜빡임 방지 */
  const handleNavEnter = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setMegaMenuOpen(true);
  };
  const handleNavLeave = () => {
    hideTimerRef.current = setTimeout(() => setMegaMenuOpen(false), 80);
  };

  return (
    <header className="tw-bg-[#2A3547] tw-sticky tw-top-0 tw-z-50 tw-relative">

      {/* ── 데스크탑 ── */}
      <div
        className="tw-max-w-[1320px] tw-mx-auto tw-px-4 tw-h-[60px] tw-hidden lg:tw-flex tw-items-center tw-gap-4"
        onMouseEnter={handleNavEnter}
        onMouseLeave={handleNavLeave}
      >

        {/* 로고 */}
        <a
          href="#"
          className="tw-bg-white tw-rounded-[35px] tw-px-[18px] tw-py-[5px] tw-flex tw-items-center tw-flex-shrink-0 tw-no-underline"
          onClick={(e) => { e.preventDefault(); movePage('/intro'); }}
        >
          <img src={image} alt="비즈톡" className="tw-max-h-[30px]" />
        </a>

        {/* 메인 메뉴 버튼들 */}
        <nav className="tw-flex tw-items-center tw-gap-10">
          {NAV_ITEMS.map((item) => (
            // tw-h-[60px]: 헤더 전체 높이와 동일하게 → tw-top-[60px]이 헤더 하단에 정확히 위치
            <div key={item.path} className="tw-relative tw-h-[60px] tw-flex tw-items-center">
              <button
                onClick={() => movePage(item.path)}
                className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0"
              >
                {item.isActive(windowPath) ? (
                  <strong className="tw-bg-white tw-rounded-[30px] tw-text-gray-900 tw-px-5 tw-py-[6px] tw-font-medium tw-text-[16px] tw-whitespace-nowrap tw-inline-block">
                    {item.label}
                  </strong>
                ) : (
                  <span className="tw-text-gray-400 hover:tw-text-white tw-text-[16px] tw-whitespace-nowrap tw-px-5 tw-py-[6px] tw-inline-block">
                    {item.label}
                  </span>
                )}
              </button>

              {/* 서브메뉴 컬럼: 버튼 기준 CSS left: 50% + translateX(-50%) → JS 측정 없이 완벽 정렬 */}
              {megaMenuOpen && (
                <div
                  className="tw-absolute tw-top-[60px] tw-left-1/2 tw-z-50 tw-min-w-[140px] tw-text-center"
                  style={{ transform: 'translateX(-50%)' }}
                >
                  <ul className="tw-list-none tw-m-0 tw-p-0 tw-pt-5">
                    {item.subItems.map((sub) => (
                      <li key={sub.path} className="tw-pb-5">
                        <a
                          href={sub.path}
                          className="tw-no-underline tw-block tw-whitespace-nowrap"
                          style={{ fontSize: 15, color: '#212529' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--bs-primary)')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#212529')}
                        >
                          {sub.isActive(windowPath)
                            ? <strong style={{ fontWeight: 500 }}>{sub.label}</strong>
                            : sub.label
                          }
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* 우측 유저 영역 */}
        <div className="tw-ml-auto tw-flex tw-items-center tw-gap-3 tw-text-sm tw-font-medium">
          {isLoggedIn ? (
            <>
              <div className="point-info-box">
                <span className="point-label">보유 포인트</span>
                <span className="point-value">{stringUtil.formatNumber(points)} P</span>
              </div>

              <div className="tw-relative" ref={accountRef}>
                <button
                  onClick={() => setAccountOpen((v) => !v)}
                  className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-gray-300 hover:tw-text-white tw-flex tw-items-center"
                >
                  <span className="material-symbols-rounded">account_circle</span>
                </button>
                {accountOpen && (
                  <ul className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-bg-white tw-shadow-md tw-rounded tw-list-none tw-m-0 tw-p-1 tw-min-w-[120px] tw-z-50">
                    <li>
                      <a href="/mypage" className="tw-block tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-text-blue-500 tw-no-underline">
                        My Page
                      </a>
                    </li>
                    <li>
                      <button
                        onClick={() => apiUtil.signOut()}
                        className="tw-block tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-text-blue-500 tw-bg-transparent tw-border-0 tw-cursor-pointer"
                      >
                        로그아웃
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </>
          ) : (
            <>
              <button className="btn btn-light btn-sm" onClick={() => movePage('/account/login')}>로그인</button>
              <button className="btn btn-light btn-sm" onClick={() => movePage('/register/agree')}>회원가입</button>
            </>
          )}
        </div>
      </div>

      {/* 메가메뉴 배경: 전체 너비 흰 패널. 컬럼은 각 버튼 wrapper 안에 있으므로 배경만 담당 */}
      {megaMenuOpen && (
        <div
          className="tw-hidden lg:tw-block tw-absolute tw-top-[60px] tw-left-0 tw-right-0 tw-bg-white tw-shadow-md tw-border-b-2 tw-border-[#2A3547] tw-z-40"
          style={{ height: megaMenuHeight }}
          onMouseEnter={handleNavEnter}
          onMouseLeave={handleNavLeave}
        />
      )}

      {/* ── 모바일 ── */}
      <div className="lg:tw-hidden tw-flex tw-items-center tw-justify-between tw-px-4 tw-h-[60px]">
        <a href="/recommend" className="tw-bg-white tw-rounded-[35px] tw-px-[18px] tw-py-[5px] tw-flex tw-items-center tw-no-underline">
          <img src={image} alt="비즈톡" className="tw-max-h-[30px]" />
        </a>
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-white tw-p-2"
        >
          <span className="material-symbols-rounded tw-text-2xl">
            {mobileOpen ? 'close' : 'menu'}
          </span>
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:tw-hidden tw-bg-[#2A3547] tw-border-t tw-border-gray-600">
          <ul className="tw-list-none tw-m-0 tw-p-0">
            {NAV_ITEMS.map((item) => (
              <MobileNavItem key={item.path} item={item} windowPath={windowPath} />
            ))}
            {isLoggedIn ? (
              <>
                <li className="tw-border-b tw-border-gray-600 tw-px-4 tw-py-3">
                  <a href="/mypage" className="tw-text-gray-300 hover:tw-text-white tw-no-underline tw-text-sm">My Page</a>
                </li>
                <li className="tw-border-b tw-border-gray-600 tw-px-4 tw-py-3">
                  <button
                    onClick={() => apiUtil.signOut()}
                    className="tw-text-left tw-text-gray-300 hover:tw-text-white tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-sm tw-p-0"
                  >
                    로그아웃
                  </button>
                </li>
              </>
            ) : (
              <li className="tw-border-b tw-border-gray-600 tw-px-4 tw-py-3 tw-flex tw-gap-2">
                <a href="/account/login" className="tw-text-gray-300 hover:tw-text-white tw-no-underline tw-text-sm">로그인</a>
                <span className="tw-text-gray-600">|</span>
                <a href="/register/agree" className="tw-text-gray-300 hover:tw-text-white tw-no-underline tw-text-sm">회원가입</a>
              </li>
            )}
          </ul>
        </div>
      )}

    </header>
  );
};

/* ── 모바일 아코디언 아이템 ── */

interface MobileNavItemProps {
  item: NavItem;
  windowPath: string;
}

const MobileNavItem: React.FC<MobileNavItemProps> = ({ item, windowPath }) => {
  const [open, setOpen] = useState(false);

  return (
    <li className="tw-border-b tw-border-gray-600">
      <button
        onClick={() => setOpen((v) => !v)}
        className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-transparent tw-border-0 tw-cursor-pointer"
      >
        <span className={`tw-text-sm ${item.isActive(windowPath) ? 'tw-text-white tw-font-semibold' : 'tw-text-gray-300'}`}>
          {item.label}
        </span>
        <span className="material-symbols-rounded tw-text-gray-400 tw-text-base">
          {open ? 'expand_less' : 'expand_more'}
        </span>
      </button>
      {open && (
        <ul className="tw-list-none tw-m-0 tw-p-0 tw-bg-[#1e2a3a]">
          {item.subItems.map((sub) => (
            <li key={sub.path}>
              <a
                href={sub.path}
                className="tw-block tw-px-8 tw-py-2.5 tw-text-sm tw-no-underline"
                style={{ color: sub.isActive(windowPath) ? '#fff' : 'var(--bs-gray-400)' }}
              >
                {sub.isActive(windowPath) ? <strong>{sub.label}</strong> : sub.label}
              </a>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default Header;
